import React, { useCallback } from 'react';
import { useRouter } from 'next/router';
import type { GetServerSideProps, GetServerSidePropsResult } from 'next';
import Image from 'next/image';
import { wrapper } from 'redux-store';

import logEvent from 'analytics/logEvent';
import type { I18nProps } from 'app.types';

import { ProcessingPageAbstract } from 'containers/payments/ProcessingPageAbstract';
import { ProductsList } from 'containers/payments/ProductsList';

import { premiumFeaturesService } from 'domains/pages/premium-features';

import { setUserAgent, setRegion } from 'redux-store/slices/ui';

import type { FeatureProps } from 'components/domains/common/Feature';
import { FeatureList } from 'components/domains/common/FeatureList';
import { ProductMini } from 'components/domains/payments/ProductMini';
import { H2, H3 } from 'components/domains/typography/Typography';
import Page from 'components/layouts/Page';

import { startSpanAndSetTransaction } from 'utils/APMService';
import getTranslate from 'utils/getTranslate';

import styles from './styles.module.scss';

const items: FeatureProps[] = [
  {
    id: 1,
    icon: '/pagesImages/premium-features/1.png',
    title: 'Unlimited health insights 24/7',
    text: 'See how your body is doing and what it needs to thrive — anytime you want',
  },
  {
    id: 2,
    icon: '/pagesImages/premium-features/2.png',
    title: 'Simple, personalized guidance',
    text: 'Expert tips crafted to help you feel better today and in the years ahead',
  },
  {
    id: 3,
    icon: '/pagesImages/premium-features/3.png',
    title: 'Detailed heartbeat analysis',
    text: 'Your stress, energy, and focus scores, plus 44 extra metrics for you to be in charge of your health with no effort',
  },
  {
    id: 4,
    icon: '/pagesImages/premium-features/4.png',
    title: 'Smart sleep analysis',
    text: 'Track your sleep cycles with 4 smart metrics and get tips to fix your sleep issues and wake up refreshed',
  },
  {
    id: 5,
    icon: '/pagesImages/premium-features/5.png',
    title: 'Full blood pressure analysis',
    text: 'Get insights and guidance to keep your numbers in check and pay timely visits to your doctor',
  },
  {
    id: 6,
    icon: '/pagesImages/premium-features/6.png',
    title: 'Complete productivity analysis',
    text: 'Get an overview of your workday, see what impacts your productivity, and stay efficient without burning out',
  },
  {
    id: 7,
    icon: '/pagesImages/premium-features/7.png',
    title: 'Comprehensive lifestyle analysis',
    text: 'Discover the habits that improve your health and those that harm it',
  },
  {
    id: 8,
    icon: '/pagesImages/premium-features/8.png',
    title: 'Real-time experiments',
    text: 'Check if your new mindfulness practice or gym membership work for you before spending too much time and money',
  },
  {
    id: 9,
    icon: '/pagesImages/premium-features/9.png',
    title: 'Personalized daily move goals',
    text: 'See if your body is up for a challenge or a well-deserved rest day based on the goals tailored to your body’s needs',
  },
  {
    id: 10,
    icon: '/pagesImages/premium-features/10.png',
    title: 'Workout zones for Apple Watch',
    text: 'Real-time heart rate zones, fine-tuned daily to your health scores to help you work out smarter and see great results',
  },
  {
    id: 11,
    icon: '/pagesImages/premium-features/11.png',
    title: 'Unlimited data storage',
    text: 'Go past 30-day limit to keep all your data as long as you want and track key trends and changes',
  },
  {
    id: 12,
    icon: '/pagesImages/premium-features/12.png',
    title: 'Direct data sync',
    text: 'Connect Fitbit, Garmin, or Withings directly for a top-notch data analysis',
  },
  {
    id: 13,
    icon: '/pagesImages/premium-features/13.png',
    title: 'Smart whereabouts analysis',
    text: 'Unlock a visual narrative of your daily routine to see where you spend your time and how it makes your body feel',
  },
];

const PremiumFeaturesPage: React.FC = () => {

  const onClickFeature = useCallback((id: string | number) => {
    logEvent('Web_Feature_Clicked', {
      feature_num: id,
    });
  }, []);

  return (
    <Page
      withoutFooter
      fullWidth
      hideLinkInHeader
      withoutLogo
      className={styles.page}
    >
      <ProcessingPageAbstract
        screen="premium-features"
      >
        <div className={styles.container}>
          <div className={styles.logo}>
            <div className={styles.logoContent}>
              <Image
                src="/pagesImages/premium-features/logo.png"
                width={188}
                height={88}
              />
            </div>
          </div>
          <H2 className={styles.center}>
            Your special offer on <br /> Welltory Premium
          </H2>
          <ProductsList component={ProductMini} />
          <H3 className={styles.title}>your premium features</H3>
          <FeatureList items={items} onClick={onClickFeature} />
        </div>
      </ProcessingPageAbstract>
    </Page>
  );
};

export default PremiumFeaturesPage;

export const getServerSideProps: GetServerSideProps =
  wrapper.getServerSideProps(
    store =>
      async (context): Promise<GetServerSidePropsResult<I18nProps>> => {
        const endSpan = startSpanAndSetTransaction(context.resolvedUrl);

        await premiumFeaturesService.server({ context, store });

        const region = (context.req.headers['cf-ipcountry'] as string) || '';
        if (region) {
          await store.dispatch(setRegion(region));
        }

        await store.dispatch(setUserAgent(context.req.headers['user-agent']));

        endSpan();

        return {
          props: {
            ...(await getTranslate(context)),
          },
        };
      },
  );
